import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ColumnDef,
  ColumnPinningState,
  ColumnSizingState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

// --- Types ---

interface ColumnConfig {
  selector: string;
  name: string;
  width: number;
  [key: string]: any;
}

interface UseGridTableSetupOptions {
  heads: ColumnConfig[];
  rows: any[];
  onResizeColumn: (column: any) => void;
  containerWidth: number;
}

/**
 * Computes column sizing from heads config, scaling up proportionally
 * to fill the container when total column width is smaller.
 */
function computeColumnSizing(heads: ColumnConfig[], containerWidth: number): ColumnSizingState {
  const sizing: ColumnSizingState = {};
  let total = 0;

  heads.forEach((h) => {
    const w = h.width || 150;
    sizing[h.selector] = w;
    total += w;
  });

  // Scale up to fill container (never scale down)
  if (containerWidth > 0 && total > 0 && total < containerWidth) {
    const scale = containerWidth / total;
    for (const key of Object.keys(sizing)) {
      sizing[key] = Math.floor(sizing[key] * scale);
    }
  }

  return sizing;
}

/**
 * Custom hook that encapsulates all @tanstack/react-table state management:
 * - Column sizing state + auto-fit to container width
 * - Column pinning state + external sync + toggle handler
 * - Column definitions
 * - Table instance creation
 * - Resize-end persistence
 */
export function useGridTableSetup({
  heads,
  rows,
  onResizeColumn,
  containerWidth,
}: UseGridTableSetupOptions) {
  // --- Column sizing state ---
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    computeColumnSizing(heads, containerWidth)
  );

  // Sync column sizing when heads or container width change.
  // When heads change: recompute from head config with scaling.
  // When only containerWidth changes: scale current sizing proportionally.
  useEffect(() => {
    setColumnSizing((prev) => {
      // Check if the set of columns changed (added/removed/reordered)
      const headKeys = heads.map((h) => h.selector);
      const prevKeys = Object.keys(prev);
      const columnsChanged =
        headKeys.length !== prevKeys.length || headKeys.some((k, i) => k !== prevKeys[i]);

      // Check if any head width values changed
      let headWidthsChanged = false;
      if (!columnsChanged) {
        for (const h of heads) {
          // Compare against the original head width, not the scaled value
          if (prev[h.selector] !== h.width) {
            headWidthsChanged = true;
            break;
          }
        }
      }

      if (columnsChanged || headWidthsChanged) {
        // Columns changed: recompute from head config with scaling
        return computeColumnSizing(heads, containerWidth);
      }

      // Only containerWidth changed: scale current sizing proportionally
      if (containerWidth > 0) {
        const total = Object.values(prev).reduce((s, v) => s + v, 0);
        if (total < containerWidth) {
          const scale = containerWidth / total;
          const next: ColumnSizingState = {};
          let changed = false;
          for (const [k, v] of Object.entries(prev)) {
            const scaled = Math.floor(v * scale);
            next[k] = scaled;
            if (scaled !== v) changed = true;
          }
          return changed ? next : prev;
        }
      }

      return prev;
    });
  }, [heads, containerWidth]);

  // --- Column pinning: defaults to pinning "name" column ---
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(() => {
    const hasName = heads.some((h) => h.selector === "name");
    return { left: hasName ? ["name"] : [], right: [] };
  });

  // Sync column pinning when heads load asynchronously
  useEffect(() => {
    const hasName = heads.some((h) => h.selector === "name");
    if (hasName) {
      setColumnPinning((prev) => {
        const leftPinned = prev.left || [];
        if (!leftPinned.includes("name")) {
          return { ...prev, left: ["name", ...leftPinned] };
        }
        return prev;
      });
    }
  }, [heads]);

  // --- Column definitions ---
  const columnDefs = useMemo<ColumnDef<any, any>[]>(
    () =>
      heads.map((head) => ({
        id: head.selector,
        accessorFn: () => null,
        size: head.width || 150,
        minSize: 50,
        maxSize: 800,
        enableResizing: true,
      })),
    [heads]
  );

  // --- Table instance ---
  const table = useReactTable({
    data: rows,
    columns: columnDefs,
    state: { columnSizing, columnPinning },
    onColumnSizingChange: setColumnSizing,
    onColumnPinningChange: setColumnPinning,
    columnResizeMode: "onChange",
    enableColumnResizing: true,
    getCoreRowModel: getCoreRowModel(),
  });

  // --- Persist column width when resize ends ---
  const prevResizingRef = useRef<string | false>(false);
  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;

  useEffect(() => {
    if (prevResizingRef.current && !isResizingColumn) {
      const colId = prevResizingRef.current;
      const head = heads.find((h) => h.selector === colId);
      const newWidth = columnSizing[colId];
      if (head && newWidth && Math.round(newWidth) !== head.width) {
        onResizeColumn({ ...head, width: Math.round(newWidth), isResizing: false });
      }
    }
    prevResizingRef.current = isResizingColumn;
  }, [isResizingColumn]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Style helper for pinned columns ---
  const getPinnedStyle = useCallback((column: any, isHeader: boolean): CSSProperties => {
    const isPinned = column.getIsPinned();
    if (!isPinned) return {};
    const style: CSSProperties = {
      position: "sticky",
      zIndex: isHeader ? 30 : 10,
    };
    if (isPinned === "left") {
      style.left = `${column.getStart("left")}px`;
    } else {
      style.right = `${column.getAfter("right")}px`;
    }
    return style;
  }, []);

  return {
    table,
    getPinnedStyle,
  };
}
